import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, RotateCcw, Save } from "lucide-react";

// Editable invite wording with a live branded preview. Placeholders: {name}, {courses}, {portal}.
export const EmailPreviewDialog = ({ open, onClose, name, courseIds = [], courseTitles = [] }) => {
  const [settings, setSettings] = useState(null);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const key = JSON.stringify([name, courseIds, courseTitles]);

  useEffect(() => {
    if (!open) return;
    api.get("/lms/admin/lms-settings").then(({ data }) => { setSettings(data); setMessage(data.invite_message); });
  }, [open]);

  useEffect(() => {
    if (!open || !settings) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const [n, ids, titles] = JSON.parse(key);
      api.post("/lms/admin/invite/preview", { name: n || "", course_ids: ids, course_titles: titles, message }).then(({ data }) => setPreview(data)).catch(() => {});
    }, 350);
    return () => clearTimeout(timer.current);
  }, [open, settings, message, key]);

  const save = async (body, msg) => {
    setSaving(true);
    try { const { data } = await api.put("/lms/admin/lms-settings", body); setSettings(data); if (body.invite_message !== undefined) setMessage(data.invite_message); toast.success(msg); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };
  const dirty = settings && message !== settings.invite_message;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="email-preview-dialog">
        <DialogHeader>
          <DialogTitle>Welcome email</DialogTitle>
          <DialogDescription className="text-xs">This is exactly what new learners receive. Use {"{name}"}, {"{courses}"} and {"{portal}"} to fill in their details.</DialogDescription>
        </DialogHeader>
        {!settings ? <Loader2 className="mx-auto animate-spin text-slate-400" /> : (
          <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="min-h-[260px] text-sm" data-testid="email-message" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" disabled={!dirty || saving} onClick={() => save({ invite_message: message }, "Saved — all future invites use this wording")} data-testid="email-save"><Save size={12} /> Save wording</Button>
                <Button size="sm" variant="outline" className="gap-1" disabled={settings.is_default_message && !dirty} onClick={() => save({ invite_message: "" }, "Back to the standard wording")} data-testid="email-reset"><RotateCcw size={12} /> Use standard wording</Button>
              </div>
              {dirty && <p className="text-[11px] text-amber-700" data-testid="email-unsaved">Unsaved changes — invites still use the saved wording until you click Save.</p>}
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                <div><p className="text-sm font-medium">Automatic reminders</p><p className="text-[11px] text-muted-foreground">Re-send this invite 3 and 7 days later to anyone who hasn't signed in. Checked daily at 9am.</p></div>
                <Switch checked={settings.reminders_enabled} disabled={saving} onCheckedChange={(v) => save({ reminders_enabled: v }, v ? "Reminders on" : "Reminders off")} data-testid="email-reminders-switch" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground" data-testid="email-subject">Subject: <span className="font-medium text-slate-900">{preview?.subject || "…"}</span></p>
              <iframe title="Email preview" srcDoc={preview?.html || ""} sandbox="" className="h-[560px] w-full rounded-lg border border-border bg-white" data-testid="email-preview-frame" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
